import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Storage keys
export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to attach Authorization header
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(error);
    }
);

// A single in-flight refresh shared by all concurrent 401s. Without this,
// every simultaneous request that receives a 401 would fire its own refresh
// call — and when the refresh token is expired they would all fail together,
// each independently triggering a logout/redirect. See clearSessionAndLogout.
let refreshPromise: Promise<string> | null = null;

// Guards against firing the logout/redirect more than once when several
// requests fail their refresh at the same time.
let isLoggingOut = false;

/**
 * Clear the session and send the user back to login exactly once. Any
 * subsequent call while a logout is already in progress is a no-op, so an
 * expired refresh token can never spin up repeated refresh/redirect attempts.
 */
function clearSessionAndLogout(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);

    if (isLoggingOut) {
        return;
    }
    isLoggingOut = true;

    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
}

/**
 * Perform a single token refresh. Uses the bare axios module (not apiClient)
 * so a 401 from the refresh endpoint does NOT re-enter this interceptor and
 * cannot cause a refresh loop — it rejects, and the caller logs the user out.
 */
async function performTokenRefresh(refreshToken: string): Promise<string> {
    const response = await axios.post<{
        access_token: string;
        refresh_token: string;
        token_type: string;
        expires_in: number;
    }>(`${API_URL}/api/v1/auth/refresh`, {
        refresh_token: refreshToken,
    });

    const { access_token, refresh_token: newRefreshToken } = response.data;

    // Store new tokens
    localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

    return access_token;
}

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
        return response;
    },
    async (error: AxiosError): Promise<never> => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Only a 401 on a request we haven't already retried is refreshable.
        if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
            return Promise.reject(error);
        }
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        // No refresh token — nothing to refresh with, log the user out.
        if (!refreshToken) {
            clearSessionAndLogout();
            return Promise.reject(error);
        }

        try {
            // Share a single refresh across all concurrent 401s.
            if (!refreshPromise) {
                refreshPromise = performTokenRefresh(refreshToken).finally(() => {
                    refreshPromise = null;
                });
            }
            const accessToken = await refreshPromise;

            // Refresh succeeded — retry the original request with the new token.
            if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }
            return apiClient(originalRequest);
        } catch (refreshError) {
            // Refresh failed (e.g. refresh token expired → 401). Do NOT retry
            // the refresh; clear the session and return to login.
            clearSessionAndLogout();
            return Promise.reject(refreshError);
        }
    }
);

export default apiClient;
